import Message from "@/components/Message/Message";
import useSmartQueryClient from "@/hooks/useSmartQueryClient";
import useTranslation from "@/hooks/useTranslation";
import { Button, Modal, message } from "antd";
import { ReactElement, useState } from "react";
import { useUploadProductBom } from "../../useProducts";
import ProductsHowItWorks from "./ProductsHowItWorks";
import UploadDragger, { OnSuccesfulFileSelection } from "./UploadDragger";
import styles from "./UploadProductAction.module.scss";
import UploadStatus from "./UploadStatus";
import ValidationStatus from "./ValidationStatus";

export type UploadProductActionStatus =
  | "choosing"
  | "validating"
  | "validated"
  | "uploading"
  | "uploaded";

export default function UploadProductAction({
  productId,
  scenarioId,
  onSuccess,
  renderer,
  children,
}: {
  productId?: number;
  scenarioId?: number;
  renderer?: (props: { onClick: () => void }) => ReactElement;
  onSuccess?: () => void;
  children?: ReactElement;
}) {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [fileName, setFileName] = useState<string>();
  const [s3FileName, setS3FileName] = useState<string>();
  const [uuid, setUuid] = useState<string>();
  const [error, setError] = useState<string>();
  const [status, setStatus] = useState<UploadProductActionStatus>("choosing");
  const smartQueryClient = useSmartQueryClient();
  const uploadBomMutation = useUploadProductBom();
  const t = useTranslation();

  const onCancel = () => {
    setModalIsOpen(false);
  };

  const onSuccesfulUpload = () => {
    setStatus("uploaded");
    smartQueryClient.invalidateQueries(["products"]);
    onSuccess?.();
  };

  const onSuccesfulValidation = () => {
    setStatus("validated");
  };

  const onSuccesfulFileSelection: OnSuccesfulFileSelection = ({ fileName, s3FileName, uuid }) => {
    setStatus("validating");
    setFileName(fileName);
    setS3FileName(s3FileName);
    setUuid(uuid);
  };

  const onUpload = () => {
    if (s3FileName && fileName) {
      uploadBomMutation
        .mutateAsync({ fileName: s3FileName, productId, scenarioId })
        .then((result) => {
          setStatus("uploading");
          setUuid(result.data.uuid);
        });
    } else {
      setStatus("choosing");
      message.error(t.errors.general);
    }
  };

  const onClick = () => {
    setModalIsOpen(true);
    setFileName(undefined);
    setS3FileName(undefined);
    setUuid(undefined);
    setStatus("choosing");
  };

  return (
    <>
      {renderer ? renderer({ onClick }) : <span onClick={onClick}>{children}</span>}
      {modalIsOpen && (
        <Modal
          footer={
            <div
              style={{
                padding: "16px 32px",
                display: "flex",
                gap: "16px",
                justifyContent: "flex-end",
              }}
            >
              <Button onClick={onCancel}>Cancel</Button>
              {["validated", "uploading"].includes(status) ? (
                <Button
                  type="primary"
                  onClick={onUpload}
                  disabled={status === "uploading" || uploadBomMutation.isLoading}
                  loading={status === "uploading" || uploadBomMutation.isLoading}
                >
                  Upload
                </Button>
              ) : status === "uploaded" ? (
                <Button
                  type="primary"
                  onClick={onCancel}
                >
                  Close
                </Button>
              ) : (
                ""
              )}
            </div>
          }
          width={1500}
          open={modalIsOpen}
          onCancel={onCancel}
          onOk={onUpload}
          className={styles.UploadProductAction}
        >
          <div className={styles.container}>
            <div className={styles.header}>
              <div>Upload BOM</div>
            </div>
            <div className={styles.upload}>
              <div className={styles.uploader}>
                <UploadDragger
                  productId={productId}
                  onError={setError}
                  onSuccess={onSuccesfulFileSelection}
                />
              </div>
              {error && <Message type="error">{error}</Message>}
            </div>
            {uuid && fileName && s3FileName && ["validating", "validated"].includes(status) ? (
              <ValidationStatus
                key={uuid}
                onSuccesfulValidation={onSuccesfulValidation}
                uuid={uuid}
                fileName={fileName}
              />
            ) : uuid && fileName && s3FileName && ["uploading", "uploaded"].includes(status) ? (
              <UploadStatus
                key={uuid}
                onSuccesfulUpload={onSuccesfulUpload}
                uuid={uuid}
                fileName={fileName}
              />
            ) : (
              <div style={{ padding: 32 }}>
                <ProductsHowItWorks />
              </div>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}
